import { useState, useEffect, useRef, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import ProtocolResponseView from "./components/ProtocolResponseView";
import { mockProtocolResponse, mockNoDataResponse, type ProtocolResponse } from "@/mocks/protocolResponses";
import type { UserProfile } from "@/mocks/data";
import { aiService } from "@/services/api";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  protocolData?: ProtocolResponse;
  citations?: Citation[];
  timestamp: string;
  consultaId?: string;
}

interface Citation {
  fuente: string;
  seccion: string;
  articulo: string;
}

const exampleQueries = [
  { label: "Agresión verbal a profesor", query: "Un estudiante insultó gravemente a un profesor durante la clase" },
  { label: "Agresión entre estudiantes", query: "Dos estudiantes se agredieron físicamente en el patio durante el recreo" },
  { label: "Apoderado en sala sin autorización", query: "Un apoderado ingresó sin autorización a la sala de clases a increpar a un profesor" },
  { label: "Vaporizador en el baño", query: "Estudiante fue sorprendido con un vaporizador en el baño del colegio" },
  { label: "Contenido en redes sociales", query: "Un estudiante publicó contenido inapropiado en redes sociales fuera del horario escolar" },
];

export default function ConsultaPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, "util" | "revisar" | null>>({});
  const [expandedCitation, setExpandedCitation] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q");

  useEffect(() => {
    if (initialQuery) {
      setInput(initialQuery);
      
      const userMsg: Message = {
        id: `msg-${Date.now()}`,
        role: "user",
        content: initialQuery,
        timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
      };

      setMessages([userMsg]);
      setLoading(true);

      async function runInitialQuery() {
        try {
          const data = await aiService.consultaNormativa(initialQuery);
          const assistantMsg: Message = {
            id: `msg-${Date.now() + 1}`,
            role: "assistant",
            content: data.respuesta || "No se recibió respuesta de la IA.",
            consultaId: data.consulta_id,
            timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
          };
          setMessages((prev) => [...prev, assistantMsg]);
        } catch (err: any) {
          console.error("Error running initial query:", err);
          const errorMsg: Message = {
            id: `msg-${Date.now() + 1}`,
            role: "assistant",
            content: `Error al conectar con la IA: ${err.response?.data?.detail || err.message}`,
            timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
          };
          setMessages((prev) => [...prev, errorMsg]);
        } finally {
          setLoading(false);
        }
      }
      runInitialQuery();
    }
  }, [initialQuery]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const queryText = input.trim();
    const userMsg: Message = {
      id: `msg-${Date.now()}`,
      role: "user",
      content: queryText,
      timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const data = await aiService.consultaNormativa(queryText);
      const assistantMsg: Message = {
        id: `msg-${Date.now() + 1}`,
        role: "assistant",
        content: data.respuesta || "No se recibió respuesta de la IA.",
        consultaId: data.consulta_id,
        timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      console.error("Error in handleSubmit:", err);
      const errorMsg: Message = {
        id: `msg-${Date.now() + 1}`,
        role: "assistant",
        content: `Error al conectar con la IA: ${err.response?.data?.detail || err.message}`,
        timestamp: new Date().toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (msgId: string, type: "util" | "revisar") => {
    const msg = messages.find((m) => m.id === msgId);
    if (msg && msg.consultaId) {
      try {
        await aiService.submitFeedback(msg.consultaId, type === "util");
      } catch (err) {
        console.error("Error submitting feedback:", err);
      }
    }
    setFeedbackGiven((prev) => ({ ...prev, [msgId]: type }));
  };

  const handleExampleClick = (query: string) => {
    setInput(query);
    inputRef.current?.focus();
  };

  const toggleCitation = (msgId: string) => {
    setExpandedCitation((prev) => (prev === msgId ? null : msgId));
  };

  if (!user) return null;

  return (
    <>
      <div className="flex flex-col h-[calc(100vh-140px)] max-w-5xl mx-auto">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto space-y-5 pb-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-20 h-20 flex items-center justify-center mb-4">
                <img src="/logos/mascota.png" alt="Mascota Asistente" className="w-full h-full object-contain drop-shadow-md" />
              </div>
              <h2 className="text-lg font-semibold text-foreground-900 mb-2">
                Asistente Normativo Escolar
              </h2>
              <p className="text-sm text-foreground-500 max-w-md mb-2">
                Escribe una situación en lenguaje natural. La IA te responderá con el protocolo aplicable,
                pasos de actuación, responsables y cita normativa basada exclusivamente en los documentos
                de tu colegio.
              </p>
              <p className="text-xs text-foreground-400 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-6 flex items-center gap-1.5">
                <i className="ri-information-line text-amber-500" />
                Las respuestas son orientativas. Siempre valida con el responsable institucional.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {exampleQueries.map((eq) => (
                  <button
                    key={eq.query}
                    onClick={() => handleExampleClick(eq.query)}
                    className="text-xs text-foreground-600 bg-background-100 hover:bg-background-200 hover:text-foreground-900 px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap border border-transparent hover:border-background-300"
                  >
                    {eq.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "user" ? (
                <div className="max-w-[80%]">
                  <div className="bg-primary-500 text-white rounded-2xl rounded-br-md px-4 py-3">
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <p className="text-xs text-white/60 mt-1 text-right">{msg.timestamp}</p>
                  </div>
                </div>
              ) : (
                <div className="w-full max-w-full">
                  {/* Assistant header */}
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-robot-line text-primary-600 text-xs" />
                    </div>
                    <span className="text-xs font-medium text-primary-600">Ezer Convivencia360</span>
                    <span className="text-xs text-foreground-400">{msg.timestamp}</span>
                  </div>

                  {/* Protocol response view */}
                  {msg.protocolData && (
                    <ProtocolResponseView
                      data={msg.protocolData}
                      onFeedback={(type) => handleFeedback(msg.id, type)}
                      feedbackGiven={feedbackGiven[msg.id] || null}
                    />
                  )}

                  {/* Legacy text response fallback */}
                  {!msg.protocolData && msg.content && (
                    <>
                      <Card className="rounded-2xl rounded-bl-md border-background-200">
                        <div
                          className="text-sm text-foreground-700 leading-relaxed space-y-2"
                          dangerouslySetInnerHTML={{
                            __html: msg.content
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground-900">$1</strong>')
                              .replace(/^- (.*)/gm, '<li class="ml-4 list-disc text-foreground-700">$1</li>')
                              .replace(/^\d\. (.*)/gm, '<li class="ml-4 list-decimal text-foreground-700">$1</li>')
                              .replace(/\n\n/g, '</p><p class="mb-2">')
                              .replace(/\n/g, '<br />'),
                          }}
                        />
                      </Card>
                      {msg.consultaId && (
                        <div className="flex items-center gap-2 mt-2 ml-1">
                          <span className="text-xs text-foreground-400">¿Te fue útil esta respuesta?</span>
                          <button
                            onClick={() => handleFeedback(msg.id, "util")}
                            className={`p-1 px-2 rounded hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1 ${
                              feedbackGiven[msg.id] === "util" ? "text-emerald-600 bg-emerald-50 font-bold" : "text-foreground-450"
                            }`}
                          >
                            <i className="ri-thumb-up-line text-xs" />
                            <span className="text-[10px]">Sí</span>
                          </button>
                          <button
                            onClick={() => handleFeedback(msg.id, "revisar")}
                            className={`p-1 px-2 rounded hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1 ${
                              feedbackGiven[msg.id] === "revisar" ? "text-red-650 bg-red-50 font-bold" : "text-foreground-450"
                            }`}
                          >
                            <i className="ri-thumb-down-line text-xs" />
                            <span className="text-[10px]">No, reportar brecha</span>
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Citations footer */}
                  {msg.citations && msg.citations.length > 0 && !msg.protocolData && (
                    <div className="mt-3 ml-1">
                      <button
                        onClick={() => toggleCitation(msg.id)}
                        className="flex items-center gap-1.5 text-xs text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-link" />
                        {msg.citations.length} citas normativas
                        {expandedCitation === msg.id ? (
                          <i className="ri-arrow-up-s-line text-sm" />
                        ) : (
                          <i className="ri-arrow-down-s-line text-sm" />
                        )}
                      </button>
                      {expandedCitation === msg.id && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {msg.citations.map((cite, i) => (
                            <span key={i} className="text-xs text-foreground-600 bg-background-100 border border-background-200 px-2.5 py-1 rounded-full">
                              {cite.fuente} · {cite.seccion}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex justify-start w-full">
              <div className="w-full">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-line text-primary-600 text-xs" />
                  </div>
                  <span className="text-xs font-medium text-primary-600">Ezer Convivencia360</span>
                </div>
                <Card className="rounded-xl border-background-200">
                  <div className="flex items-center gap-3 py-2">
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2.5 h-2.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2.5 h-2.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                    <span className="text-sm text-foreground-400">Analizando situación y recuperando protocolo aplicable...</span>
                  </div>
                </Card>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="sticky bottom-0 bg-background-50 pt-3">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder="Describe la situación... (ej: Un estudiante insultó a un profesor durante la clase)"
                rows={2}
                className="w-full resize-none rounded-xl border-2 border-background-300 bg-white px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all outline-none"
                disabled={loading}
              />
            </div>
            <Button
              type="submit"
              loading={loading}
              disabled={!input.trim() || loading}
              size="lg"
              className="self-end rounded-xl"
            >
              <i className="ri-send-plane-fill text-lg" />
            </Button>
          </form>
          <p className="text-xs text-foreground-400 text-center mt-2">
            Ezer Convivencia360 responde solo con información del reglamento de tu colegio. Las respuestas son orientativas.
          </p>
        </div>
      </div>
    </>
  );
}